/**
 * aC Men's Hub の課題管理の「やること」（振り分け・判定・確認のカード）と、カードから行う操作の API 契約（zod schema + 型）。
 *
 * カードは保存せず、読むたびに台帳から組み立てる（期限切れなど、時間がたつと変わる値を持たないため）。
 * 振り分けのカードは未振り分けの論点1件につき1枚、判定・確認のカードは課題ごとに次にやること（判定表 BR-19）の1枚。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の「やること」のカード（振り分け・判定・確認）と、AI が除外した投稿の一覧、カードからの操作（課題ではない・あとで・声の要点を直す・このままでよい・返信済み・直った・まだ直っていない・まだ対応中・振り分けに戻す・週次の記録・滞留通知）の API 契約を API と Web で共有する
 * @graph-connects none
 */
import { z } from 'zod';
import { IssueNoPointReasonSchema } from './issue-ingest.js';
import { IssueJobTriggerSchema } from './issue-mgmt-backlog.js';
import { ISSUE_TITLE_MAX_LENGTH, IssueIdSchema, IssueReviewWeekdaySchema, ManualVoiceSourceSchema, PointIdSchema, PointStatusSchema, VoiceOriginSchema, } from './issue-mgmt.js';
import { IssueNextActionSchema, NextActionSeveritySchema } from './issue-mgmt-status.js';
/**
 * 「課題ではない」の理由（BR-06）。質問・案内で完結／連絡・共有のみ／一時的で対応不要／既に解決済み／その他／理由なし。
 * 人には選ばせず、AI の理由案を記録する（案が無ければ none。D33）。
 * @graph-connects none
 */
export const IssueNotIssueReasonSchema = z.enum([
    'question_answered',
    'notice_only',
    'temporary',
    'already_resolved',
    'other',
    'none',
]);
/**
 * やることのカードの種類（絞り込みの単位）。triage＝振り分け、judge＝判定（判定表の行1・2・5・6）、
 * check＝確認（行3・4・7〜11）。
 * @graph-connects none
 */
export const TodoCardTypeSchema = z.enum(['triage', 'judge', 'check']);
/**
 * カードに共通する項目。
 * - cardId: カードを一意に決める鍵（振り分けは論点の番号、判定・確認は課題の番号と次にやることの種類）
 * - who: 受け持つ人（当番・判定者・担当。決まっていなければ null）。mine は自分が受け持つカードか
 * - dueDate・overdue・since: 期限・期限切れ・期限の無いカードの出た日
 * - severity: 並び順に使う急ぎ具合（画面には出さない）
 * @graph-connects none
 */
const TodoCardBaseSchema = z.object({
    cardId: z.string(),
    type: TodoCardTypeSchema,
    who: z.object({ email: z.string(), displayName: z.string() }).nullable(),
    mine: z.boolean(),
    dueDate: z.string().nullable(),
    overdue: z.boolean(),
    since: z.string().nullable(),
    severity: NextActionSeveritySchema,
});
/**
 * 振り分けのカード（未振り分けの論点1件）。point は論点、voice はその元の声。
 * - point.aiDraft: 声の要点を AI が書いたままか（AI下書きの札を出す）
 * - point.status: 開いた時点の振り分けの状態（unassigned か、戻ってきた later）。操作の expectedStatus に渡す
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることの振り分けのカード（声の要点・原文・チャンネル・非公開の印・投稿日時・期限）を表す
 * @graph-connects none
 */
export const TodoTriageCardSchema = TodoCardBaseSchema.extend({
    source: z.literal('triage'),
    point: z.object({
        pointId: z.string(),
        title: z.string(),
        aiDraft: z.boolean(),
        status: PointStatusSchema,
    }),
    voice: z.object({
        voiceId: z.string(),
        origin: VoiceOriginSchema,
        body: z.string().nullable(),
        bodyDeleted: z.boolean(),
        channelName: z.string().nullable(),
        isPrivate: z.boolean(),
        postedAt: z.string().nullable(),
        postedDate: z.string().nullable(),
        slackUrl: z.string().nullable(),
        manualSource: ManualVoiceSourceSchema.nullable(),
    }),
});
/**
 * 判定・確認のカード（課題1件の次にやること）。中身は課題の詳細から読む。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることの判定・確認のカード（課題の番号・タイトル・次にやること）を表す
 * @graph-connects none
 */
export const TodoIssueCardSchema = TodoCardBaseSchema.extend({
    source: z.literal('issue'),
    issueId: z.string(),
    title: z.string(),
    next: IssueNextActionSchema,
});
/**
 * やることのカード1枚。
 * @graph-connects none
 */
export const TodoCardSchema = z.discriminatedUnion('source', [
    TodoTriageCardSchema,
    TodoIssueCardSchema,
]);
/**
 * やることの応答。cards は並び順（急ぎ具合 → 期限の近い順 → 種類）。
 * weekly は週次の曜日・今日がその日か・残りの判定のカードの数（全員の分）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることのカードの一覧（並び順どおり）と、週次の曜日・今日が週次の日か・残りの判定のカードの数を返す
 * @graph-connects none
 */
export const TodoListResponseSchema = z.object({
    cards: z.array(TodoCardSchema),
    weekly: z.object({
        weekday: IssueReviewWeekdaySchema,
        isToday: z.boolean(),
        judgeCount: z.number().int(),
    }),
});
/**
 * AI が除外した投稿の一覧に、既定で出す日数（直近30日）。
 * @graph-connects none
 */
export const EXCLUDED_VOICE_DAYS = 30;
/**
 * AI が除外した投稿の1行。
 * @graph-connects none
 */
export const ExcludedVoiceSchema = z.object({
    voiceId: z.string(),
    postedAt: z.string().nullable(),
    postedDate: z.string().nullable(),
    channelName: z.string().nullable(),
    isPrivate: z.boolean(),
    text: z.string(),
    reason: IssueNoPointReasonSchema.nullable(),
    slackUrl: z.string().nullable(),
});
/**
 * AI が除外した投稿の一覧の条件（新しい順に先頭 limit 件）。
 * @graph-connects none
 */
export const ExcludedVoicesQuerySchema = z.object({
    limit: z.number().int().min(1).max(200).optional(),
});
/**
 * AI が除外した投稿の一覧の応答。total は直近30日の全件数、items は新しい順の先頭 limit 件（既定 20）。
 * @graph-connects none
 */
export const ExcludedVoicesResponseSchema = z.object({
    total: z.number().int(),
    items: z.array(ExcludedVoiceSchema),
});
/**
 * 振り分けのカードの「課題ではない」「あとで」の入力。expectedStatus・expectedIssueId は開いた時点の論点の状態
 * （違えば先に振り分けた人を返す）。
 * @graph-connects none
 */
export const PointTriageInputSchema = z.object({
    pointId: PointIdSchema,
    action: z.enum(['not_issue', 'later']),
    expectedStatus: PointStatusSchema,
    expectedIssueId: IssueIdSchema.nullable(),
});
/**
 * 声の要点（論点のタイトル）を直す入力。base は開いた時点の要点（違えば保存しない）。空にはできない。
 * @graph-connects none
 */
export const PointTitleUpdateInputSchema = z.object({
    pointId: PointIdSchema,
    base: z.string(),
    value: z.string().trim().min(1).max(ISSUE_TITLE_MAX_LENGTH),
});
/**
 * 「このままでよい」の入力（再浮上・判定の変更を確かめた）。base は開いた時点の判定日時と確かめた時刻。
 * @graph-connects none
 */
export const IssueAcknowledgeInputSchema = z.object({
    issueId: IssueIdSchema,
    base: z.object({ decidedAt: z.string().nullable(), reviewedAt: z.string().nullable() }),
});
/**
 * 「返信済みにする」の入力（カードに並んだ声の論点の番号）。
 * @graph-connects none
 */
export const IssueRepliedInputSchema = z.object({
    issueId: IssueIdSchema,
    pointIds: z.array(PointIdSchema).min(1).max(200),
});
/**
 * 課題1件を指す入力（直った・まだ直っていない・まだ対応中・判定カードを開いた）。
 * @graph-connects none
 */
export const IssueRefInputSchema = z.object({ issueId: IssueIdSchema });
/**
 * 週次の判定の記録の結果。recorded は今日が週次の日で記録したか。
 * @graph-connects none
 */
export const ReviewEventResultSchema = z.object({ recorded: z.boolean() });
/**
 * 滞留通知の1回の結果。sent＝送った、nothing＝滞留が無く週次の日でもないので送らなかった、
 * failed＝送れなかった（送り先のチャンネルが決まっていない・Slack に断られた）。
 * @graph-connects none
 */
export const StaleNotifyResultSchema = z.enum(['sent', 'nothing', 'failed']);
/**
 * 滞留通知の最後の実行。staleCount は数えた滞留の件数。
 * @graph-connects none
 */
export const StaleNotifyRunSchema = z.object({
    at: z.string(),
    trigger: IssueJobTriggerSchema,
    triggeredBy: z.string().nullable(),
    result: StaleNotifyResultSchema,
    staleCount: z.number().int(),
});
/**
 * 滞留通知の状況（設定タブの定期処理の行）。lastRun は最後の実行、lastFailedAt は送れなかった最後の時刻
 * （その後に送れていれば null）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の滞留通知の状況（最後の実行と結果・前回送れなかった時刻）を表す
 * @graph-connects none
 */
export const StaleNotifyStatusSchema = z.object({
    lastRun: StaleNotifyRunSchema.nullable(),
    lastFailedAt: z.string().nullable(),
});
//# sourceMappingURL=issue-todo.js.map